'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';

import { LogoHelper } from '@hooks/useCommonData';
import { cn } from '@libs/utils';

interface LogoImageProps {
  variant?: 'full' | 'icon' | 'animated';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  theme?: 'light' | 'dark' | 'auto';
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
}

export default function LogoImage({
  variant = 'full',
  size = 'md',
  theme = 'auto',
  alt = 'PropX Logo',
  className = '',
  width,
  height,
}: LogoImageProps) {
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const updateTheme = () => {
      if (theme === 'auto') {
        const isDark =
          window.matchMedia?.('(prefers-color-scheme: dark)').matches ||
          document.documentElement.classList.contains('dark');
        setCurrentTheme(isDark ? 'dark' : 'light');
      } else {
        setCurrentTheme(theme);
      }
    };

    updateTheme();

    // Listen for theme changes
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    const handleThemeChange = () => updateTheme();

    mediaQuery?.addEventListener('change', handleThemeChange);
    window.addEventListener('themeChange', handleThemeChange);

    return () => {
      mediaQuery?.removeEventListener('change', handleThemeChange);
      window.removeEventListener('themeChange', handleThemeChange);
    };
  }, [theme]);

  const logoPath = LogoHelper.getPath(variant, currentTheme);
  const logoSize = LogoHelper.getSize(variant, size);

  // Default dimensions based on variant and size
  const getDefaultDimensions = () => {
    const baseDimensions = {
      icon: { sm: 24, md: 32, lg: 48, xl: 64 },
      full: { sm: 80, md: 120, lg: 150, xl: 200 },
      animated: { sm: 128, md: 192, lg: 256, xl: 320 },
    };

    const dimension = baseDimensions[variant][size];

    if (variant === 'icon') {
      return { width: dimension, height: dimension };
    } else if (variant === 'full') {
      return { width: dimension, height: dimension * 0.4 }; // Aspect ratio for full logo
    } else {
      return { width: dimension, height: dimension * 0.9 }; // Aspect ratio for animated logo
    }
  };

  const defaultDimensions = getDefaultDimensions();

  return (
    <Image
      src={logoPath}
      alt={alt}
      width={width || defaultDimensions.width}
      height={height || defaultDimensions.height}
      className={cn(logoSize, className)}
      priority={variant === 'full' || variant === 'animated'}
    />
  );
}
